import { useCallback, useEffect, useRef, useState } from "react";
import type { IntakeItem } from "src/admin/modules/order-return/intake/types.ts";

/**
 * Очередь приёмки: какие строки ещё в работе, какая из них текущая и когда
 * ввод заблокирован.
 *
 * Одна на оба устройства. Раньше порядок решался в двух местах — телефон брал
 * шаг с сервера, компьютер вёл собственный курсор в хуке фокуса на 271
 * строку, — и правило «что следующее» расходилось при первой же правке.
 * Теперь правило одно, а различается только то, чем его двигают: клавиатурой
 * за компьютером и сканом на телефоне.
 *
 * Чего здесь нет намеренно: фокуса браузера и клавиатуры. Это способ ввода, а
 * не очередь, и у телефона его нет вовсе.
 */

/**
 * Коробка по артикулам: одинаковые стоят подряд.
 *
 * Один и тот же товар приезжает двумя позициями заказа — докупали, или
 * позиции разошлись по цене, — и в списке они оказывались в разных его
 * концах. Оператор держит в руках вещь, а не позицию: он ищет «такое же» и
 * обязан видеть все её строки рядом, иначе вторую находят в конце круга и
 * приходуют на другую полку.
 *
 * Порядок между артикулами не меняется: группа встаёт туда, где стояла её
 * первая строка. Список остаётся картой коробки, а не алфавитом.
 *
 * Части набора едут следом за своим набором: в списке их нет, но порядок
 * `items` общий на весь экран, и отрывать часть от родителя незачем.
 */
export const groupedByArticle = (items: IntakeItem[]): IntakeItem[] => {
  const keyOf = (item: IntakeItem) =>
    item.article.sku ?? item.article.title ?? item.id;

  const groups = new Map<string, IntakeItem[]>();

  items
    .filter((item) => item.set_parent_item_id === null)
    .forEach((item) => {
      const group = groups.get(keyOf(item));

      if (group === undefined) {
        groups.set(keyOf(item), [item]);

        return;
      }

      group.push(item);
    });

  const partsOf = (parentId: string) =>
    items.filter((entry) => entry.set_parent_item_id === parentId);

  return [...groups.values()]
    .flat()
    .flatMap((item) => [item, ...partsOf(item.id)]);
};

/**
 * Строки, по которым ещё есть работа.
 *
 * Серая сюда не попадает: клиент её не называл, ждать нечего, и закроет её
 * кнопка завершения. Развёрнутый набор — тоже: принять его нечем, он не вещь,
 * в работе его части, и они в этом же списке.
 */
export const workableItems = (items: IntakeItem[]): IntakeItem[] => {
  const expanded = new Set(
    items
      .map((item) => item.set_parent_item_id)
      .filter((id): id is string => id !== null),
  );

  /*
    Серость наследуется от набора к частям.

    Клиент обещает набор, а не бутылки: если набор не обещан, не обещана и
    ни одна его часть. Без этого развёрнутый серый набор подкидывался в
    работу частями — экран разворачивал карточку с состояниями и полками,
    пока счётчик честно говорил «0 из 0», а кнопка завершения отказывала.
  */
  const greyParents = new Set(
    items
      .filter(
        (item) =>
          item.intake_result === "pending" &&
          item.expectation === "not_expected",
      )
      .map((item) => item.id),
  );

  return items.filter(
    (item) =>
      item.intake_result === "pending" &&
      item.expectation !== "not_expected" &&
      !expanded.has(item.id) &&
      !(
        item.set_parent_item_id !== null &&
        greyParents.has(item.set_parent_item_id)
      ),
  );
};

/**
 * Сколько штук из коробки разобрано.
 *
 * Считаем штуки, а не строки: позиция на три единицы, принятая целиком,
 * должна давать «3 из 3». По строкам прогресс врёт на каждом количестве — и
 * врёт в успокаивающую сторону, показывая работу законченной раньше, чем она
 * закончена.
 *
 * Части набора в счёт не идут: в коробке лежит набор, а не три его части, и
 * считает его строка-родитель. Серая нетронутая не считается вовсе — ни в
 * сделанные, ни в общее число: «6 из 7» после возвращения её в работу читается
 * правильно, «7 из 7» при неразобранной — нет.
 *
 * Одна функция на оба устройства и на ленту шагов: расхождение между лентой
 * и списком читается складом как поломка.
 */
export const intakeProgress = (
  items: IntakeItem[],
): { done: number; total: number } => {
  const counted = items.filter(
    (item) =>
      item.set_parent_item_id === null &&
      (item.intake_result !== "pending" ||
        item.expectation !== "not_expected" ||
        // Серая, которую уже взяли в руки, в счёт возвращается: иначе
        // счётчик показывает «5 из 5», пока кнопка завершения отказывает
        // словами «в руках ещё вещь». Главное число экрана и главная
        // кнопка экрана не должны спорить.
        (item.scanned_at ?? null) !== null),
  );

  const units = (item: IntakeItem) =>
    Math.max(1, Math.trunc(Number(item.quantity) || 1));

  return {
    done: counted
      .filter((item) => item.intake_result !== "pending")
      .reduce((sum, item) => sum + units(item), 0),
    total: counted.reduce((sum, item) => sum + units(item), 0),
  };
};

export const useIntakeQueue = ({
  items,
  suggestedItemId,
  currentItemId,
  isBusy,
}: {
  items: IntakeItem[];
  /** Что сервер предлагает следующим, пока в руках ничего нет. */
  suggestedItemId: string | null;
  /** Что сервер держит в руках: факт склада, а не предпочтение экрана. */
  currentItemId: string | null;
  isBusy: boolean;
}) => {
  const [chosenId, setChosenId] = useState<string | null>(null);
  const chosenRef = useRef<string | null>(null);
  chosenRef.current = chosenId;

  /** Где в списке стояла текущая строка — ориентир после её приёма. */
  const lastIndexRef = useRef(0);

  const workable = workableItems(items);
  const queue = workable.map((item) => item.id).join(",");

  /*
    Выбрать можно и то, по чему очередь не идёт.

    Развёрнутый набор в работе не числится: принять его нечем, работают
    части. Но карточку с частями открывает именно он, и пока выбор
    принимался только из очереди, клик по набору в списке не делал ничего.
    Заметно это становится на втором наборе: пока активна часть первого,
    карточка просто не меняется — строка не подсвечивается, работать не с
    чем.

    Очередь от этого не меняется: стрелки и «что следующее» по-прежнему
    ходят по `workable`, набор лишь разрешено сделать текущим.
  */
  const expandedSets = items.filter((item) =>
    items.some((entry) => entry.set_parent_item_id === item.id),
  );

  const isSelectable = (itemId: string | null) =>
    itemId !== null &&
    (workable.some((item) => item.id === itemId) ||
      expandedSets.some((item) => item.id === itemId));

  // Выбор только переносится, но не заводится сам: пока человек ничего не
  // выбрал, текущую строку называет сервер (см. ниже). Иначе выбор
  // перебивал бы вещь в руках, и на телефоне, где выбирать нечем, очередь
  // расходилась бы с тем, что показывает экран.
  useEffect(() => {
    if (chosenRef.current === null) {
      return;
    }

    const stillThere =
      workable.some((item) => item.id === chosenRef.current) ||
      expandedSets.some((item) => item.id === chosenRef.current);

    if (stillThere) {
      lastIndexRef.current = items.findIndex(
        (item) => item.id === chosenRef.current,
      );

      return;
    }

    // Разобранная строка отдаёт очередь соседу снизу, а не первой строке
    // списка: оператор идёт по коробке сверху вниз, и прыжок в начало после
    // каждого приёма возвращал его к уже разобранному.
    const next =
      workable.find(
        (item) =>
          items.findIndex((entry) => entry.id === item.id) >=
          lastIndexRef.current,
      ) ?? workable[0];

    setChosenId(next?.id ?? null);
    // Пересчитываем по составу очереди, а не по каждому рендеру.
  }, [queue]);

  /**
   * Текущая строка.
   *
   * Выбор человека главнее: он только что нажал стрелку или кликнул, и
   * перебивать это сервером значит отнимать у него клавиатуру. Пока выбора
   * нет, называет сервер: сперва вещь в руках — это факт склада, она лежит
   * на столе, — потом подсказка очереди, потом просто первая в работе.
   */
  const activeId =
    (isSelectable(chosenId) ? chosenId : null) ??
    currentItemId ??
    suggestedItemId ??
    workable[0]?.id ??
    null;

  const step = useCallback(
    (direction: 1 | -1) => {
      if (workable.length === 0) {
        return;
      }

      const index = workable.findIndex((item) => item.id === activeId);
      const next = index === -1 ? 0 : index + direction;

      if (next < 0 || next >= workable.length) {
        return;
      }

      setChosenId(workable[next].id);
    },
    [queue, activeId],
  );

  /**
   * Ввод заблокирован, пока предмет меняется.
   *
   * Зажатый Enter гасится защитой от повторной отправки только до ответа
   * сервера. После него предмет уже другой, а клавиша всё ещё нажата — и
   * второе срабатывание прилетает в следующую строку с чужими значениями.
   * Поэтому блокировка снимается не ответом, а кадром после смены предмета.
   */
  const [isHandover, setIsHandover] = useState(false);

  useEffect(() => {
    setIsHandover(true);

    const settle = window.setTimeout(() => setIsHandover(false), 0);

    return () => window.clearTimeout(settle);
  }, [activeId]);

  return {
    workable,
    activeId,
    setActiveId: setChosenId,
    step,
    isLocked: isBusy || isHandover,
  };
};
